/**
 * assignCipSlots — Contract C1 (BL produces → BR renders → TR config).
 *
 * Pure ranking function that decides which captured photo-ID fills CIP slot
 * ID1 vs ID2. Replaces the three divergent CIP-write paths + the silent-drop
 * behaviour the inspection found (slot-3 C.2 / slot-1 G8): when a higher-
 * priority ID arrives it takes ID1 and bumps the incumbent to ID2 instead of
 * being dropped.
 *
 * Bill's worked example (brief §2.5): NRA + Passport captured → Passport fills
 * ID1. A Driver's License is then captured → DL takes ID1, Passport bumps to
 * ID2. The priority order is admin-configured per `/v3/config/cip-id-priority`
 * (Contract C5, TR-owned). This function is PURE — the caller fetches the
 * priority rules and passes them in; it never reads config itself.
 *
 * NEVER drops a captured ID: every capture is ranked; the best fills ID1, the
 * second-best fills ID2. Core Director exposes exactly two CIP ID slots, so a
 * third+ captured ID can't be slotted — it is returned in `overflow` (never
 * silently discarded) so the surfacing layer can warn rather than lose it.
 *
 * ── Core Director field destinations ────────────────────────────────────────
 * ID1 → F254 (type) · F255 (number) · F256 (issuer) · F257 (issue) · F258 (expiry).
 * ID2 → SPEC says F802–806; today's code writes F814/801/813/815/816. That
 * drift is OPEN and owned by TR (slot-3 D.4) — this module is destination-
 * agnostic (it emits the slot VALUES; BR maps them to whatever CD numbers TR
 * confirms). Do not resolve the drift here.
 */
// TODO(cd-fieldnum): ID2 destination is F802–806 (SPEC) vs F814/801/813/815/816
// (code). TR confirms the canonical CD numbers; this fn stays destination-agnostic.

/**
 * One admin-configured priority rule. Sourced from `/v3/config/cip-id-priority`
 * (`CipIdPriorityRule{customerType,idTypeCode,rank,isActive}` per Contract C5).
 */
export interface CipIdPriorityRule {
  /**
   * Customer/entity type the rule applies to (e.g. 'Individual', 'NonCitizen').
   * A blank string or '*' matches any type (global rule).
   */
  customerType: string;
  /**
   * The ID type this rule ranks. Matched case-insensitively against the
   * capture's `idTypeCode` (CD code like '1'/'3') OR its `docTypeKey` (slug
   * like 'us-drivers-license'/'passport') so callers can rank by whichever
   * key they have. */
  idTypeCode: string;
  /** 1 = highest priority (fills ID1 first). Lower number wins. */
  rank: number;
  isActive: boolean;
}

/** A captured photo-ID candidate for CIP slotting. */
export interface CapturedIdDoc {
  /** CD ID type code (e.g. '1'=DL, '2'=State ID, '3'=Passport). May be ''. */
  idTypeCode: string;
  /**
   * Doc-type slug from the capture pipeline ('us-drivers-license', 'passport',
   * 'state-id', 'green-card', …). Used as a fallback match key against the
   * priority rules when `idTypeCode` doesn't match a rule. */
  docTypeKey?: string;
  idNumber?: string | null;
  idIssuer?: string | null;
  idIssueDate?: string | null;
  idExpiryDate?: string | null;
  /** ISO timestamp — stable tie-break when two captures share a rank. */
  capturedAt?: string | null;
  /** Originating tile/docCode, carried through for traceability. */
  sourceDocCode?: string;
}

/** The value written to a CIP slot. Destination CD numbers are BR's concern. */
export interface CipSlotValue {
  idType: string;
  idNumber: string;
  idIssuer: string;
  idIssueDate: string;
  idExpiryDate: string;
  /** Which captured doc filled this slot (for the derived-section "from X" note). */
  sourceDocCode?: string;
}

export interface CipSlotAssignment {
  id1: CipSlotValue | null;
  id2: CipSlotValue | null;
  /**
   * Captured IDs beyond the two CD slots, ranked. Never silently dropped — the
   * surfacing layer can warn "3 IDs captured; CD holds 2". Empty in the common
   * 1–2 ID case.
   */
  overflow: CipSlotValue[];
}

export interface AssignCipSlotsOptions {
  /** Restrict priority rules to this customer/entity type (plus global rules). */
  customerType?: string;
}

const RANK_UNRANKED = Number.MAX_SAFE_INTEGER;

function norm(s: string | null | undefined): string {
  return (s ?? '').trim().toLowerCase();
}

/** A rule matches a customer type when it's global or equals the requested type. */
function ruleMatchesCustomerType(rule: CipIdPriorityRule, customerType?: string): boolean {
  const rt = norm(rule.customerType);
  if (rt === '' || rt === '*') return true;
  if (!customerType) return true; // no filter requested → don't exclude on type
  return rt === norm(customerType);
}

/** Resolve a capture's rank from the active rules; unranked → RANK_UNRANKED. */
function rankFor(
  capture: CapturedIdDoc,
  rules: ReadonlyArray<CipIdPriorityRule>,
  customerType?: string,
): number {
  const codeKey = norm(capture.idTypeCode);
  const slugKey = norm(capture.docTypeKey);
  let best = RANK_UNRANKED;
  for (const rule of rules) {
    if (!rule.isActive) continue;
    if (!ruleMatchesCustomerType(rule, customerType)) continue;
    const ruleKey = norm(rule.idTypeCode);
    if (ruleKey === '') continue;
    if (ruleKey === codeKey || (slugKey !== '' && ruleKey === slugKey)) {
      if (rule.rank < best) best = rule.rank;
    }
  }
  return best;
}

function toSlotValue(c: CapturedIdDoc): CipSlotValue {
  return {
    idType: c.idTypeCode ?? '',
    idNumber: c.idNumber ?? '',
    idIssuer: c.idIssuer ?? '',
    idIssueDate: c.idIssueDate ?? '',
    idExpiryDate: c.idExpiryDate ?? '',
    ...(c.sourceDocCode ? { sourceDocCode: c.sourceDocCode } : {}),
  };
}

/**
 * Rank captured photo-IDs and assign CIP slots. Stable: equal ranks break by
 * `capturedAt` (earliest first), then original capture order — so re-running
 * with the same inputs is deterministic and a freshly-captured higher-priority
 * ID deterministically claims ID1.
 */
export function assignCipSlots(
  captures: ReadonlyArray<CapturedIdDoc>,
  priority: ReadonlyArray<CipIdPriorityRule>,
  opts?: AssignCipSlotsOptions,
): CipSlotAssignment {
  if (!captures || captures.length === 0) {
    return { id1: null, id2: null, overflow: [] };
  }
  const customerType = opts?.customerType;

  const ranked = captures.map((capture, index) => ({
    capture,
    index,
    rank: rankFor(capture, priority, customerType),
    ts: capture.capturedAt ? Date.parse(capture.capturedAt) : Number.NaN,
  }));

  ranked.sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    const at = Number.isNaN(a.ts) ? Number.MAX_SAFE_INTEGER : a.ts;
    const bt = Number.isNaN(b.ts) ? Number.MAX_SAFE_INTEGER : b.ts;
    if (at !== bt) return at - bt;
    return a.index - b.index;
  });

  const slots = ranked.map((r) => toSlotValue(r.capture));
  return {
    id1: slots[0] ?? null,
    id2: slots[1] ?? null,
    overflow: slots.slice(2),
  };
}
